import React, { useEffect, useState } from 'react';

interface PaginatedTableProps {
  onPageChange: (page: number) => void;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  children: React.ReactNode;
}

const PaginatedTable = (props: PaginatedTableProps) => {
  const [currentPageInput, setCurrentPageInput] = useState(String(props.currentPage + 1));

  useEffect(() => {
    setCurrentPageInput(String(props.currentPage + 1));
  }, [props.currentPage]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentPageInput.trim() === '') {
        setCurrentPageInput('1');
        props.onPageChange(0);
      } else {
        const page = keepPageInputWithinBounds(currentPageInput);
        if (page !== props.currentPage) {
          props.onPageChange(page);
        }
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [currentPageInput]);

  const keepPageInputWithinBounds = (value: string) => 
    Math.max(1, Math.min(parseInt(currentPageInput, 10), props.totalPages)) - 1;

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || /^[1-9]\d*$/.test(value)) {
      setCurrentPageInput(value);
    }
  };

  const handlePageBlur = () => {
    if (currentPageInput.trim() === '' || parseInt(currentPageInput, 10) < 1) {
      setCurrentPageInput('1');
    }
  };

  const handlePageChange = (page: number) => {
    props.onPageChange(page);
  };

  return <>
    {props.children}
    <div className="d-flex justify-content-between align-items-center w-100 my-1">
      <div className="d-flex justify-content-center flex-grow-1">
        <div className="pagination">
          <button
            className="page-item page-link"
            onClick={() => handlePageChange(0)}
            disabled={props.currentPage <= 0}
            aria-label="First Page">
            <i className="bi bi-chevron-bar-left" />
          </button>

          <button
            className="page-item page-link"
            onClick={() => handlePageChange(props.currentPage - 1)}
            disabled={props.currentPage <= 0}
            aria-label="Previous Page">
            <i className="bi bi-chevron-left" />
          </button>

          <div className="d-flex align-items-center">
            <input
              className="page-item page-link"
              type="number"
              value={currentPageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageBlur}
              min={1}
              max={props.totalPages}
              style={{
                textAlign: 'center',
                width: '75px',
                border: '1px solid #ccc',
                borderRadius: '4px', 
                padding: '4px'
              }} />
            <div className="mx-1">of {props.totalPages}</div>
          </div>

          <button
            className="page-item page-link"
            onClick={() => handlePageChange(props.currentPage + 1)}
            disabled={props.currentPage >= props.totalPages - 1}
            aria-label="Next Page">
            <i className="bi bi-chevron-right" />
          </button>

          <button
            className="page-item page-link"
            onClick={() => handlePageChange(props.totalPages - 1)}
            disabled={props.currentPage >= props.totalPages - 1}
            aria-label="Last Page">
            <i className="bi bi-chevron-bar-right" />
          </button>
        </div>
      </div>
    </div>
  </>
};

export default PaginatedTable;